import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ServicesPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter(s => s.category === activeCategory));
    }
  }, [activeCategory, services]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
      setFilteredServices(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load services');
      setLoading(false);
    }
  };

  const handleApply = (serviceId) => {
    if (!user) {
      toast.error('Please login to apply');
      navigate('/');
      return;
    }
    navigate(`/apply/${serviceId}`);
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      {/* Header */}
      <section style={{ padding: '4rem 2rem 2rem', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <button 
            onClick={() => navigate('/')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              background: 'none', 
              border: 'none', 
              color: '#FF6B35', 
              cursor: 'pointer',
              marginBottom: '2rem',
              fontWeight: '600'
            }}
            data-testid="back-to-home-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </button>
          
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', color: '#0F172A' }}>
            Our Services
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569', maxWidth: '700px' }}>
            Choose from a wide range of financial services tailored to your needs. Apply online in minutes.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section style={{ padding: '2rem', backgroundColor: 'white', borderBottom: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveCategory('all')}
            className={activeCategory === 'all' ? 'primary-button' : 'outline-button'}
            data-testid="filter-all"
          >
            All Services
          </button>
          <button
            onClick={() => setActiveCategory('loan')}
            className={activeCategory === 'loan' ? 'primary-button' : 'outline-button'}
            data-testid="filter-loans"
          >
            Loans
          </button>
          <button
            onClick={() => setActiveCategory('banking')}
            className={activeCategory === 'banking' ? 'primary-button' : 'outline-button'}
            data-testid="filter-banking"
          >
            Banking
          </button>
          <button
            onClick={() => setActiveCategory('insurance')}
            className={activeCategory === 'insurance' ? 'primary-button' : 'outline-button'}
            data-testid="filter-insurance"
          >
            Insurance
          </button>
        </div>
      </section>

      {/* Services Grid */}
      <section style={{ padding: '4rem 2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem' }}>
              <div style={{ color: '#FF6B35', fontSize: '1.125rem' }}>Loading services...</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '2rem' }}>
              {filteredServices.map((service) => (
                <div key={service.id} className="service-card" data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div style={{ marginBottom: '1rem' }}>
                    <span 
                      style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        backgroundColor: service.category === 'loan' ? '#D1FAE5' : service.category === 'banking' ? '#DBEAFE' : '#FEF3C7',
                        color: service.category === 'loan' ? '#065F46' : service.category === 'banking' ? '#1E3A8A' : '#92400E'
                      }}
                    >
                      {service.category}
                    </span>
                  </div>
                  
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>
                    {service.name}
                  </h3>
                  
                  <p style={{ color: '#475569', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    {service.description}
                  </p>
                  
                  {service.min_amount && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.25rem' }}>
                        Amount Range
                      </div>
                      <div style={{ fontWeight: '600', color: '#0F172A' }}>
                        ₹{service.min_amount.toLocaleString()} - ₹{service.max_amount?.toLocaleString() || '∞'}
                      </div>
                    </div>
                  )}
                  
                  {service.interest_rate_range && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.25rem' }}>
                        Interest Rate
                      </div>
                      <div style={{ fontWeight: '600', color: '#FF6B35' }}>
                        {service.interest_rate_range}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleApply(service.id)}
                    className="primary-button"
                    style={{ width: '100%', justifyContent: 'center' }}
                    data-testid={`apply-${service.name.toLowerCase().replace(/\s+/g, '-')}-button`}
                  >
                    Apply Now <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={() => navigate(`/enquiry?service=${encodeURIComponent(service.name)}`)}
                    className="outline-button"
                    style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem', fontSize: '0.85rem' }}
                    data-testid={`enquire-${service.name.toLowerCase().replace(/\s+/g, '-')}-button`}
                  >
                    Quick Enquiry
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
